import { Fragment, lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { isProduction } from '../../libs/constants'
import { shouldFetchLandingSelector } from '../../reducers/landingReducer'
import { isVendorLoggingInSelector } from '../../reducers/uiReducer'
import {
  BuyerItemRoutePage,
  BuyerInstalmentRoutePage,
  BuyerPeriodicRoutePage,
} from '../buyer/BuyerItemRoutePage'
import TransactionResultPage from '../buyer/TransactionResultPage'
import LandingUserRoleValidation from '../landing/LandingUserRoleValidation'
import { isVendorLoggedInSelector } from '../libs/selectors'
import VendorLoginPage from '../login/VendorLoginPage'
import NotFound from './NotFound'
import OnLocationChange from './OnLocationChange'
import PostReactNativeMessage from './PostReactNativeMessage'

const DebugPage = isProduction ? Fragment : lazy(() => import('./DebugPage'))
const Announcement = lazy(() => import('../landing/Announcement'))
const CreateItemPage = lazy(() => import('../create-item/CreateItemPage'))
const ItemHistoryDetailPage = lazy(() =>
  import('../history/ItemHistoryDetailPage'),
)
const WithdrawPage = lazy(() => import('../withdraw/WithdrawPage'))
const InflowDetailPage = lazy(() => import('../inflow/InflowDetailPage'))
const OutflowPaymentDetailPage = lazy(() =>
  import('../outflow/OutflowPaymentDetailPage'),
)
const OutflowWithdrawDetailPage = lazy(() =>
  import('../outflow/OutflowWithdrawDetailPage'),
)
const LandingIndexPage = lazy(() => import('../landing/LandingIndexPage'))

export default function App() {
  const authenticated = useSelector(state =>
    isVendorLoggedInSelector(state.user, state.ui),
  )
  const shouldFetchLanding = useSelector(state =>
    shouldFetchLandingSelector(state.landing),
  )
  const vendorLoggingIn = useSelector(state =>
    isVendorLoggingInSelector(state.ui),
  )

  if (vendorLoggingIn) {
    return <VendorLoginPage />
  }

  if (authenticated && shouldFetchLanding) {
    return <LandingUserRoleValidation />
  }

  return (
    <BrowserRouter>
      <PostReactNativeMessage authenticated={authenticated} />
      <OnLocationChange />
      <Suspense>
        <Routes>
          <Route path="/" element={<LandingIndexPage />} />
          <Route
            path="/buyer/items/:entityId"
            element={<BuyerItemRoutePage />}
          />
          <Route
            path="/buyer/instalments/:entityId"
            element={<BuyerInstalmentRoutePage />}
          />
          <Route
            path="/buyer/orders/:entityId"
            element={<BuyerPeriodicRoutePage />}
          />
          <Route
            path="/transaction-result"
            element={<TransactionResultPage />}
          />
          {authenticated ? (
            <>
              <Route path="/announcement" element={<Announcement />} />
              <Route path="/new" element={<CreateItemPage />} />
              <Route
                path="/history/:itemId"
                element={<ItemHistoryDetailPage />}
              />
              <Route path="/withdraw" element={<WithdrawPage />} />
              <Route
                path="/inflows/:transactionId"
                element={<InflowDetailPage />}
              />
              <Route
                path="/payments/:transactionId"
                element={<OutflowPaymentDetailPage />}
              />
              <Route
                path="/withdraws/:withdrawId"
                element={<OutflowWithdrawDetailPage />}
              />
            </>
          ) : null}
          {!isProduction && <Route path="/debug" element={<DebugPage />} />}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
